import { ActionIcon, Box, Button, Text } from "@mantine/core";
import { XIcon } from "@phosphor-icons/react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../utils";
import classes from "./list.module.css";

type Props = {
  selectedCount: number;
  remainingTotal?: number | null;
  primaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    leftSection?: ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  /** Shown when the whole page is selected and more rows match the filter. */
  selectAllAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  onClear: () => void;
};

export function BulkActionBar({
  selectedCount,
  remainingTotal,
  primaryAction,
  secondaryAction,
  selectAllAction,
  onClear,
}: Props) {
  const { t } = useTranslation("common");

  if (selectedCount <= 0) {
    return null;
  }

  const summary =
    remainingTotal != null && Number.isFinite(remainingTotal)
      ? t("list.bulkSelectedWithTotal", {
          count: selectedCount,
          total: currencyFormat(remainingTotal, { withSymbol: true }),
        })
      : t("list.bulkSelected", { count: selectedCount });

  return (
    <Box className={classes.bulkBar} role="toolbar" aria-label={summary}>
      <Text className={classes.bulkText}>{summary}</Text>
      {selectAllAction && (
        <Button
          className={classes.bulkSelectAll}
          variant="transparent"
          onClick={selectAllAction.onClick}
          loading={selectAllAction.loading}
        >
          {selectAllAction.label}
        </Button>
      )}
      <Box className={classes.bulkDivider} aria-hidden />
      <Box className={classes.bulkActions}>
        {secondaryAction && (
          <Button
            className={classes.bulkBtnSecondary}
            onClick={secondaryAction.onClick}
            loading={secondaryAction.loading}
          >
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button
            className={classes.bulkBtnPrimary}
            fw={600}
            leftSection={primaryAction.leftSection}
            onClick={primaryAction.onClick}
            loading={primaryAction.loading}
          >
            {primaryAction.label}
          </Button>
        )}
        <ActionIcon
          className={classes.bulkClose}
          variant="transparent"
          onClick={onClear}
          aria-label={t("list.clearSelection")}
        >
          <XIcon size={16} weight="bold" />
        </ActionIcon>
      </Box>
    </Box>
  );
}
